import classNames from "classnames";
import pluralize from "pluralize";
import {useState} from "react";
import {AccordionEventKey} from "react-bootstrap/esm/AccordionContext";
import {useFormContext} from "react-hook-form";
import {FiX} from "react-icons/fi";

import {Alert} from "@/components/alert/Alert.tsx";
import {Button} from "@/components/button/Button.tsx";
import {Card} from "@/components/card/Card.tsx";
import {CDNImage} from "@/components/cdnImage/CDNImage.tsx";
import {Divider} from "@/components/divider/Divider.tsx";
import {RadioField} from "@/components/form/fields/RadioField/RadioField.tsx";
import {IconButton} from "@/components/iconButton/IconButton.tsx";
import {ServiceField} from "@/lib/constants/service.ts";
import {ThirdPartyService} from "@/lib/queries/checks.ts";
import {ThirdPartyMonitoringType} from "@/lib/types/checks.ts";
import {ThirdPartyComponentFilter} from "@/pages/checks/components/form/fields/components/ThirdPartyComponentFilter.tsx";

import styles from "./ThirdPartyGroup.module.scss";

interface ThirdPartyGroupProps {
  onChange: (group: ThirdPartyService | null, setGroup?: boolean) => void;
}

export const ThirdPartyGroup = ({onChange}: ThirdPartyGroupProps) => {
  const [activeAccordion, setActiveAccordion] = useState<AccordionEventKey>(null);
  const {watch, setValue, clearErrors} = useFormContext();
  const checkId = watch(ServiceField.ID);
  const group = watch(ServiceField.THIRD_PARTY_GROUP);
  if (!group) return null;
  const componentCount = group.components_count;
  const selectedGroupComponentCountString =
    componentCount !== undefined ? `${componentCount.toLocaleString()} ${pluralize("component", componentCount)}` : "";
  const monitoringType = watch(ServiceField.THIRD_PARTY_MONITORING_TYPE) as ThirdPartyMonitoringType;
  const selectSpecific = () => {
    if (monitoringType !== ThirdPartyMonitoringType.SPECIFIC) {
      setValue(ServiceField.THIRD_PARTY_MONITORING_TYPE, ThirdPartyMonitoringType.SPECIFIC, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  };

  return (
    <>
      <Card variant="cloudStatusv2" className="p-0 mt-1">
        <div className={styles.selectedGroupContainer}>
          <div className="d-flex align-items-center">
            <CDNImage className={styles.selectedGroupIcon} alt={`${group.group} logo`} src={group.logo_src} />
            <div>
              <div className="pulseTextBodyBold mb-1">{group.group}</div>
              <div className="pulseTextBodySmall pulse-color-gray-600">{selectedGroupComponentCountString}</div>
            </div>
          </div>
          {!checkId && <IconButton variant="ghost" size="fitIcon" onClick={() => onChange(null, true)} icon={FiX} />}
        </div>
        <Divider />
        <div className="p-3 d-flex flex-column gap-3">
          <Button
            onClick={() => {
              setValue(ServiceField.THIRD_PARTY_MONITORING_TYPE, ThirdPartyMonitoringType.ALL, {
                shouldDirty: true,
                shouldValidate: true,
              });
              clearErrors(ServiceField.THIRD_PARTY_COMPONENTS);
            }}
            variant="cloudStatusS"
            className={classNames({active: monitoringType === ThirdPartyMonitoringType.ALL})}
          >
            <div className="text-start">
              <RadioField
                variant="cloudStatus"
                name={ServiceField.THIRD_PARTY_MONITORING_TYPE}
                value={ThirdPartyMonitoringType.ALL}
                label={
                  <div>
                    <div className="pulseTextBodySmallBold pulse-color-gray-900 mb-2">Monitor all components</div>
                    <div className="pulseTextBodySmall pulse-color-gray-600">
                      Receive notifications for every component. Recommended only if you use all components.
                    </div>
                  </div>
                }
              />
              {monitoringType === ThirdPartyMonitoringType.ALL &&
                componentCount !== undefined &&
                componentCount >= 100 && (
                  <Alert variant="maintenance" className="py-2 mb-0 mt-2">
                    <div className="pulseTextBodySmall">
                      We strongly recommend filtering by region or service. Monitoring all{" "}
                      <span className="pulseTextBodySmallBold">{selectedGroupComponentCountString}</span> can overwhelm
                      your alert channels and make critical issues harder to spot.
                    </div>
                  </Alert>
                )}
            </div>
          </Button>
          <div
            role="button"
            tabIndex={0}
            onClick={selectSpecific}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                selectSpecific();
              }
            }}
            className={classNames(styles.filter, {
              [styles.highlighted]: monitoringType === ThirdPartyMonitoringType.SPECIFIC && activeAccordion !== null,
              [styles.selected]: monitoringType === ThirdPartyMonitoringType.SPECIFIC && activeAccordion === null,
            })}
          >
            <div className="text-start w-100">
              <RadioField
                variant="cloudStatus"
                name={ServiceField.THIRD_PARTY_MONITORING_TYPE}
                value={ThirdPartyMonitoringType.SPECIFIC}
                label={
                  <div>
                    <div className="pulseTextBodySmallBold pulse-color-gray-900 mb-2">
                      Filter to specific components
                    </div>
                    <div className="pulseTextBodySmall pulse-color-gray-600">
                      Receive official outage alerts only for selected components.
                    </div>
                  </div>
                }
              />
              {monitoringType === ThirdPartyMonitoringType.SPECIFIC && (
                <ThirdPartyComponentFilter
                  activeAccordion={activeAccordion}
                  setActiveAccordion={setActiveAccordion}
                  groupCount={selectedGroupComponentCountString}
                />
              )}
            </div>
          </div>
        </div>
      </Card>
      <Divider className="my-4" />
    </>
  );
};
